import * as Sentry from '@sentry/react'
import {BrowserTracing} from '@sentry/tracing'
import {projectName, projectVersion} from '@src/configs/project'
import {isNetworkError, makeContext, maskDeep} from '@src/utility/sentry/misc'
import {Context, SentryUser} from '@src/utility/sentry/type'
import {useEffect} from 'react'
import {createRoutesFromChildren, matchRoutes, Route, useLocation, useNavigationType} from 'react-router-dom'

export function sentryInit(user?: SentryUser): void {
  Sentry.init({
    beforeBreadcrumb: breadcrumb => {
      try {
        return maskDeep(breadcrumb, ['password', 'token']) as Sentry.Breadcrumb
      } catch (error) {
        sentryCaptureException(error as Error, 'sentryInit beforeBreadcrumb')
      }
      return breadcrumb
    },
    beforeSend(event, hint) {
      const exception = hint.originalException

      if (exception instanceof Error) {
        if (exception.message?.includes('unsafe-eval')) {
          return null
        }
      }

      return event
    },
    dsn: import.meta.env.VITE_SENTRY_DSN_URL,
    enabled: ['stage', 'sandbox', 'production'].includes(import.meta.env.VITE_ENVIRONMENT ?? 'development'),
    environment: import.meta.env.VITE_ENVIRONMENT ?? 'development',
    ignoreErrors: ['Network Error', 'AbortError', 'The user aborted a request', 'Request was aborted'],
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        )
      }),
      new Sentry.Replay({
        maskAllInputs: true
      })
    ],
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    tracesSampleRate: 1.0,
    tunnel: import.meta.env.VITE_SENTRY_TUNNEL_URL
  })
  if (user?.id) {
    sentrySetUser(user)
  }
}

export function sentrySetUser(user: SentryUser): void {
  Sentry.setUser({projectName, projectVersion, ...user})
}

export function sentryCaptureException(error: Error, title: string, context?: Context): void {
  error.name = `${title}: ${error.name}`
  !(import.meta.env.VITE_ENVIRONMENT === 'production') &&
    console.error(title, JSON.stringify(error), JSON.stringify(context))
  Sentry.captureException(error, context ? makeContext(context) : undefined)
}

export function sentryCaptureMessage(message: string, context?: Context): void {
  Sentry.captureMessage(message, context ? makeContext(context) : undefined)
}

export const sentryCaptureMessageFiltered = (error: Error, title: string, data: unknown = null): void => {
  !isNetworkError(error) &&
    sentryCaptureMessage(title, {extra: {data: JSON.stringify(data), error: JSON.stringify(error)}})
  import.meta.env.VITE_ENVIRONMENT !== 'production' &&
    console.error('sentryCaptureMessage', title, JSON.stringify(error), JSON.stringify(data))
}

export const sentryCaptureExceptionFiltered = (error: Error, title: string, data: unknown = null): void => {
  !isNetworkError(error) &&
    sentryCaptureException(error, title, {extra: {data: JSON.stringify(data), error: JSON.stringify(error)}})
  import.meta.env.VITE_ENVIRONMENT !== 'production' &&
    console.error('sentryCaptureException', title, JSON.stringify(error), JSON.stringify(data))
}

export const sentryRoute = (): typeof Route => Sentry.withSentryRouting(Route)
